import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../auth/msalConfig';
import {
  exportEngagement,
  exportQoH,
  type ExportFormat,
  type EngagementSection,
  type QoHSection,
} from '../../api/exportApi';

// ── Engagement export modal ───────────────────────────────────────────────────

const ENGAGEMENT_SECTIONS: { key: EngagementSection; label: string }[] = [
  { key: 'EngagementIndex', label: 'Engagement index + benchmarks' },
  { key: 'TrendChart',      label: 'Trend chart' },
  { key: 'DriverScores',    label: 'Driver scores + recommendations' },
  { key: 'TeamBreakdown',   label: 'Team breakdown' },
  { key: 'Comments',        label: 'Comments' },
];

interface EngagementExportModalProps {
  surveyId: string;
  surveyTitle: string;
  onClose: () => void;
}

export function EngagementExportModal({ surveyId, surveyTitle, onClose }: EngagementExportModalProps) {
  const { instance, accounts } = useMsal();
  const [format, setFormat] = useState<ExportFormat>('Pdf');
  const [sections, setSections] = useState<Set<EngagementSection>>(
    new Set(ENGAGEMENT_SECTIONS.map(s => s.key))
  );
  const [commentary, setCommentary] = useState('');
  const [status, setStatus] = useState<'idle' | 'generating' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const toggleSection = (key: EngagementSection) =>
    setSections(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const handleExport = async () => {
    if (sections.size === 0) { setErrorMsg('Select at least one section.'); return; }
    setStatus('generating');
    setErrorMsg('');
    try {
      const r = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      await exportEngagement(
        r.accessToken,
        surveyId,
        format,
        [...sections],
        commentary || undefined
      );
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setErrorMsg('Export failed. Please try again.');
    }
  };

  return (
    <Overlay onClose={onClose}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>Export — {surveyTitle}</h3>

      <FormatPicker value={format} onChange={setFormat} />

      <div style={{ marginBottom: '1rem' }}>
        <strong style={{ fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>Sections to include</strong>
        {ENGAGEMENT_SECTIONS.map(s => (
          <label key={s.key} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.35rem', cursor: 'pointer', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={sections.has(s.key)} onChange={() => toggleSection(s.key)} />
            {s.label}
          </label>
        ))}
      </div>

      <CommentaryField value={commentary} onChange={setCommentary} />

      {errorMsg && <p style={{ color: 'var(--color-error)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>{errorMsg}</p>}

      {status === 'done' && (
        <p style={{ color: 'var(--color-success)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
          ✓ Download started.
        </p>
      )}

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button className="btn btn-primary" onClick={handleExport} disabled={status === 'generating'} style={{ minWidth: 160 }}>
          {status === 'generating' ? 'Generating…' : `Download ${format.toUpperCase()}`}
        </button>
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
      </div>
    </Overlay>
  );
}

// ── QoH export modal ──────────────────────────────────────────────────────────

const QOH_SECTIONS: { key: QoHSection; label: string }[] = [
  { key: 'SummaryMetrics',     label: 'Summary metrics' },
  { key: 'ScoresBySource',     label: 'Scores by source' },
  { key: 'ScoresByDepartment', label: 'Scores by department' },
  { key: 'RoundHistory',       label: 'Round history' },
];

interface QoHExportModalProps {
  onClose: () => void;
}

export function QoHExportModal({ onClose }: QoHExportModalProps) {
  const { instance, accounts } = useMsal();
  const [format, setFormat] = useState<ExportFormat>('Pdf');
  const [sections, setSections] = useState<Set<QoHSection>>(
    new Set(QOH_SECTIONS.map(s => s.key))
  );
  const [commentary, setCommentary] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState<'idle' | 'generating' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const toggleSection = (key: QoHSection) =>
    setSections(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const handleExport = async () => {
    if (sections.size === 0) { setErrorMsg('Select at least one section.'); return; }
    setStatus('generating');
    setErrorMsg('');
    try {
      const r = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      await exportQoH(
        r.accessToken,
        format,
        [...sections],
        commentary || undefined,
        dateFrom ? new Date(dateFrom).toISOString() : undefined,
        dateTo   ? new Date(dateTo).toISOString()   : undefined
      );
      setStatus('done');
    } catch {
      setStatus('error');
      setErrorMsg('Export failed. Please try again.');
    }
  };

  return (
    <Overlay onClose={onClose}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>Export — Quality of Hire</h3>

      <FormatPicker value={format} onChange={setFormat} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
        <div>
          <label style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>Date from (optional)</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={dateInput} />
        </div>
        <div>
          <label style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>Date to (optional)</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={dateInput} />
        </div>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <strong style={{ fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>Sections to include</strong>
        {QOH_SECTIONS.map(s => (
          <label key={s.key} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.35rem', cursor: 'pointer', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={sections.has(s.key)} onChange={() => toggleSection(s.key)} />
            {s.label}
          </label>
        ))}
      </div>

      <CommentaryField value={commentary} onChange={setCommentary} />

      {errorMsg && <p style={{ color: 'var(--color-error)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>{errorMsg}</p>}
      {status === 'done' && (
        <p style={{ color: 'var(--color-success)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>✓ Download started.</p>
      )}

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button className="btn btn-primary" onClick={handleExport} disabled={status === 'generating'} style={{ minWidth: 160 }}>
          {status === 'generating' ? 'Generating…' : `Download ${format.toUpperCase()}`}
        </button>
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
      </div>
    </Overlay>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '1rem',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--color-surface)', borderRadius: 12, padding: '1.5rem',
        width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {children}
      </div>
    </div>
  );
}

function FormatPicker({ value, onChange }: { value: ExportFormat; onChange: (f: ExportFormat) => void }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <strong style={{ fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>Format</strong>
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        {(['Pdf', 'Pptx'] as ExportFormat[]).map(f => (
          <label key={f} style={{ display: 'flex', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
            <input type="radio" name="format" value={f} checked={value === f} onChange={() => onChange(f)} />
            {f === 'Pdf' ? 'PDF' : 'PowerPoint (.pptx)'}
          </label>
        ))}
      </div>
    </div>
  );
}

function CommentaryField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.4rem' }}>
        Optional commentary
      </label>
      <textarea
        rows={3}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Add context, highlights or recommendations for this report…"
        style={{ width: '100%', padding: '0.4rem 0.6rem', fontSize: '0.9rem', border: '1px solid var(--color-border)', borderRadius: 4, fontFamily: 'inherit', resize: 'vertical' }}
      />
    </div>
  );
}

const dateInput: React.CSSProperties = {
  display: 'block', width: '100%', marginTop: 4,
  padding: '0.4rem 0.6rem', fontSize: '0.9rem',
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-surface)',
};
